import React, { useEffect } from "react";
import { useTutorAvailabilityStore } from "src/store/tutorAvailabilityStore";
import AvailabilityGrid from "src/components/tutor/availability/AvailabilityGrid";
import AutoAcceptRules from "src/components/tutor/availability/AutoAcceptRules";
import GoogleCalendarIntegration from "src/components/tutor/availability/GoogleCalendarIntegration";
import TutorPreferences from "src/components/tutor/availability/TutorPreferences";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/Tabs";
import { Button } from "src/components/ui/Button";
import { Calendar, Settings, Clock, RefreshCw } from "lucide-react";
import { useToast } from "src/components/ui/UseToast";
import { useProfileData } from "src/hooks/useProfileData";
import { useAuth } from "src/context/AuthContext";
import LoadingSpinner from "src/components/LoadingSpinner";
import { usePageHeaderStore } from "src/store/pageHeaderStore";

const TutorSchedulePage: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const profileData = useProfileData();
  const {
    fetchAvailabilityData,
    setTutorId,
    isLoading: availabilityLoading,
    error: availabilityError,
  } = useTutorAvailabilityStore();
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  // Load availability data on component mount
  useEffect(() => {
    // Get tutor ID from auth user
    const tutorId = user?.id;

    if (tutorId) {
      // Set tutor ID in the availability store
      setTutorId(tutorId);

      // Fetch availability data from database
      fetchAvailabilityData();
    }
  }, [setTutorId, fetchAvailabilityData, user?.id]);

  useEffect(() => {
    setTitle("Availability Management");
    setDescription("Manage your teaching availability, set auto-accept rules, and integrate with your calendar.");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  // Determine if there's any loading or error state
  const isLoading = availabilityLoading;
  const error = availabilityError;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner size="lg" fullScreen={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <h3 className="text-red-800 font-medium">Error loading data</h3>
        <p className="text-red-700 mt-1">{error}</p>
        <Button
          variant="outline"
          className="mt-2"
          onClick={() => fetchAvailabilityData()}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Tabs defaultValue="availability" className="space-y-6">
      <TabsList className="grid grid-cols-4 w-full max-w-3xl">
        <TabsTrigger value="availability" className="flex items-center">
          <Calendar className="h-4 w-4 mr-2" />
          Availability Grid
        </TabsTrigger>
        <TabsTrigger value="auto-accept" className="flex items-center">
          <Settings className="h-4 w-4 mr-2" />
          Auto-Accept Rules
        </TabsTrigger>
        <TabsTrigger value="calendar" className="flex items-center">
          <Calendar className="h-4 w-4 mr-2" />
          Calendar Integration
        </TabsTrigger>
        <TabsTrigger value="preferences" className="flex items-center">
          <Clock className="h-4 w-4 mr-2" />
          Preferences
        </TabsTrigger>
      </TabsList>

      <TabsContent value="availability" className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">
            Interactive Availability Grid
          </h2>
          <p className="text-gray-600 mb-6">
            Click and drag to create availability windows. Click on
            existing slots to change their status.
          </p>
          <AvailabilityGrid />
        </div>
      </TabsContent>

      <TabsContent value="auto-accept" className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <AutoAcceptRules />
        </div>
      </TabsContent>

      <TabsContent value="calendar" className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <GoogleCalendarIntegration />
        </div>
      </TabsContent>

      <TabsContent value="preferences" className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <TutorPreferences />
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default TutorSchedulePage;
